import { useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import Tooltip from '../../../common/Tooltip'
import Typography from '@mui/material/Typography'
import HighlightOff from '@mui/icons-material/HighlightOff'
import { Theme } from '@mui/material/styles'

import { Input, Output } from 'common/api/v1/types'
import { Api } from '../../../../store'
import { Paper } from '../../../common/Form'
import PaginatedList from '../../../common/SelfStatePaginatedList'
import { Table } from '../../../common/Table'
import { AutoUpdatingOutputHealthIndicator as HealthIndicator } from '../../../common/Indicator'
import { inputOutputColor } from '../../../../utils'
import { EnrichedOutput, OutputsRequestParams } from '../../../../api/nm-types'

const styles = {
  paper: {
    padding: (theme: Theme) => theme.spacing(1),
    margin: (theme: Theme) => theme.spacing(0, 0, 2),
    boxShadow: 'none',
    border: (theme: Theme) => `1px solid ${theme.palette.grey['800']}`,
  },
  button: {
    color: (theme: Theme) => theme.palette.error.main,
    borderColor: (theme: Theme) => theme.palette.error.main,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    minHeight: '48px',
    minWidth: '140px',
  },
  name: {
    fontWeight: 'bold',
  },
}

const outputApi = Api.outputApi

interface RecipientsProps {
  input: Input
  onRemove: (output: Output) => void
  onCancel: (outputId: Output['id']) => void
  changes: Array<Output['id']>
}
const Recipients = ({ input, onCancel, onRemove, changes }: RecipientsProps) => {
  const [hideFilter, setHideFilter] = useState(true)
  const [total, setTotal] = useState(0)

  return (
    <Paper
      collapsible
      title={`Current recipients${total ? ` (${total})` : ''}`}
      sx={styles.paper}
      collapsed
      titleTypography="h3"
    >
      <Grid item xs={12}>
        <PaginatedList<OutputsRequestParams, EnrichedOutput>
          api={outputApi.getOutputs.bind(outputApi)}
          emptyMessage="no recipients"
          hideSearch={hideFilter}
          notFoundMessage="no matching outputs"
          onListChange={({ filter, total = 0 }) => {
            setHideFilter(!filter && total < 10)
            setTotal(total)
          }}
          otherParams={{ input: input.id }}
          searchPlaceholder="Find specific output…"
          List={({ list }) => (
            <Table
              data={list}
              isSmall
              hasBorders
              noHeader
              config={[
                {
                  getValue: ({ name, adminStatus }) => (
                    <Typography
                      component="div"
                      sx={styles.name}
                      data-test-id="output-name"
                      {...inputOutputColor({ adminStatus })}
                    >
                      {name}
                    </Typography>
                  ),
                },
                {
                  getValue: (output) => <HealthIndicator outputId={output.id} />,
                },
                {
                  getValue: ({ _group }) =>
                    !!_group && (
                      <Typography color="textSecondary" variant="body2">
                        {_group.name}
                      </Typography>
                    ),
                },
                {
                  getValue: (output) => (
                    <Box sx={styles.actions}>
                      {changes.includes(output.id) ? (
                        <Button sx={styles.button} onClick={() => onCancel(output.id)} variant="outlined" size="small">
                          Cancel remove
                        </Button>
                      ) : (
                        <Tooltip title="Remove from recipients" placement="top">
                          <IconButton edge="start" aria-label="remove" onClick={() => onRemove(output)}>
                            <HighlightOff />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  ),
                  props: {
                    align: 'right',
                  },
                },
              ]}
            />
          )}
        />
      </Grid>
    </Paper>
  )
}

export default Recipients
